import { useState } from 'react'
import {
  Avatar, Box, Button, Chip, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, IconButton, MenuItem, Select,
  Stack, Switch, TextField, Tooltip, Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError } from '@cmms/api-client'
import type { CreateSlaPolicyRequest, SlaPolicy } from '@cmms/api-client'

import { api } from '../../lib/api'
import { toast } from '../../store/snackbarStore'
import { useConfirm } from '../../components/ConfirmDialog'
import { UserAvatar } from '../../components/UserAvatar'
import { SearchableSelect } from '../../components/SearchableSelect'
import { CustomerScopePicker } from '../../components/CustomerScopePicker'
import { CustomerScopeChip } from '../../components/CustomerScopeChip'
import { formatSiteName } from '../../lib/siteDisplayName'

const PRIORITY_COLORS: Record<string, string> = { all: '#64748B', low: '#22C55E', medium: '#F59E0B', high: '#F97316', critical: '#EF4444' }

export function SlaTab() {
  const qc = useQueryClient()
  const [editPolicy, setEditPolicy] = useState<SlaPolicy | null>(null)
  const [createOpen, setCreateOpen] = useState(false)
  const confirmDlg = useConfirm()

  const policiesQ = useQuery({ queryKey: ['sla-policies'], queryFn: () => api.sla.list() })
  const policies: SlaPolicy[] = Array.isArray(policiesQ.data) ? policiesQ.data : []

  const deleteM = useMutation({
    mutationFn: (id: string) => api.sla.remove(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['sla-policies'] }); toast.success('Policy deleted') },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed'),
  })

  const applyM = useMutation({
    mutationFn: () => api.sla.applyToExisting(),
    onSuccess: (r) => { toast.success(r.updated > 0 ? `Applied SLA to ${r.updated} open work orders` : 'All open work orders already have SLA policies') },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed'),
  })

  const toggleM = useMutation({
    mutationFn: (p: SlaPolicy) => api.sla.update(p.id, { is_active: !p.is_active }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['sla-policies'] }) },
  })

  function fmtTime(mins: number) {
    if (mins < 60) return `${mins}m`
    if (mins < 1440) return `${(mins / 60).toFixed(mins % 60 ? 1 : 0)}h`
    return `${(mins / 1440).toFixed(1)}d`
  }

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
        <Box>
          <Typography sx={{ fontSize: 18, fontWeight: 700 }}>SLA Policies</Typography>
          <Typography sx={{ fontSize: 13, color: 'text.secondary', mt: 0.25 }}>
            Define response and resolution targets per priority level. Policies auto-attach to new work orders.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1.5}>
          {policies.length > 0 && (
            <Button variant="outlined" onClick={() => applyM.mutate()} disabled={applyM.isPending}
              sx={{ fontSize: 12, fontWeight: 600 }}>
              {applyM.isPending ? 'Applying...' : 'Apply to existing WOs'}
            </Button>
          )}
          <Button variant="contained" startIcon={<AddIcon sx={{ fontSize: 16 }} />} onClick={() => setCreateOpen(true)}
            sx={{ fontSize: 13, fontWeight: 600 }}>New policy</Button>
        </Stack>
      </Stack>

      {policies.length === 0 ? (
        <Box sx={{ py: 8, textAlign: 'center', border: '1px dashed', borderColor: 'divider', borderRadius: 2 }}>
          <TimerOutlinedIcon sx={{ fontSize: 36, color: 'text.disabled', mb: 1 }} />
          <Typography sx={{ fontSize: 14, color: 'text.secondary' }}>No SLA policies configured</Typography>
          <Typography sx={{ fontSize: 12.5, color: 'text.disabled', mt: 0.5 }}>Create a policy to start tracking response and resolution times</Typography>
        </Box>
      ) : (
        <Stack spacing={1.5}>
          {policies.map((p) => {
            const c = PRIORITY_COLORS[p.priority] ?? '#64748B'
            return (
              <Box key={p.id} sx={{ p: 2.5, borderRadius: 2, border: '1px solid', borderColor: 'divider',
                bgcolor: p.is_active ? 'background.paper' : (t) => t.palette.mode === 'light' ? '#FAFAFB' : 'rgba(255,255,255,0.02)',
                opacity: p.is_active ? 1 : 0.6, transition: 'all 120ms' }}>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Box sx={{ width: 42, height: 42, borderRadius: 99, bgcolor: `${c}14`, color: c,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <TimerOutlinedIcon sx={{ fontSize: 22 }} />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{p.name}</Typography>
                      <Chip label={p.priority === 'all' ? 'All priorities' : p.priority} size="small"
                        sx={{ height: 20, fontSize: 10, fontWeight: 700, bgcolor: `${c}15`, color: c, textTransform: 'capitalize' }} />
                      {p.is_default && <Chip label="Default" size="small" sx={{ height: 20, fontSize: 10, fontWeight: 700, bgcolor: 'primary.main', color: '#fff' }} />}
                      {p.site_name && <Chip label={p.site_name} size="small" sx={{ height: 20, fontSize: 10, fontWeight: 600 }} />}
                      <CustomerScopeChip customerId={p.customer_id} />
                    </Stack>
                    <Stack direction="row" spacing={3} sx={{ mt: 0.5 }}>
                      <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                        Response: <strong>{fmtTime(p.response_time)}</strong>
                      </Typography>
                      <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                        Resolution: <strong>{fmtTime(p.resolution_time)}</strong>
                      </Typography>
                      {p.escalation_to_name && (
                        <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                          Escalate to: <strong>{p.escalation_to_name}</strong> at {p.escalation_after_pct}%
                        </Typography>
                      )}
                    </Stack>
                  </Box>
                  <Stack direction="row" spacing={0.75} alignItems="center">
                    <Tooltip title={p.is_active ? 'Disable' : 'Enable'} arrow>
                      <Switch size="small" checked={p.is_active} onChange={() => toggleM.mutate(p)} />
                    </Tooltip>
                    <Tooltip title="Edit" arrow>
                      <IconButton size="small" onClick={() => setEditPolicy(p)}
                        sx={{ color: 'text.secondary', border: '1px solid', borderColor: 'divider', borderRadius: 1.25 }}>
                        <EditOutlinedIcon sx={{ fontSize: 15 }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete" arrow>
                      <IconButton size="small" onClick={() => confirmDlg({
                        title: `Delete "${p.name}"?`, message: 'Existing work orders will keep their SLA data.',
                        confirmLabel: 'Delete', variant: 'danger',
                      }).then((ok) => ok && deleteM.mutate(p.id))}
                        sx={{ color: 'text.secondary', border: '1px solid', borderColor: 'divider', borderRadius: 1.25,
                          '&:hover': { color: 'error.main' } }}>
                        <DeleteOutlineIcon sx={{ fontSize: 15 }} />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Stack>
              </Box>
            )
          })}
        </Stack>
      )}

      {(createOpen || editPolicy) && (
        <SlaPolicyDialog
          policy={editPolicy}
          onClose={() => { setCreateOpen(false); setEditPolicy(null) }}
        />
      )}
    </Box>
  )
}

function SlaPolicyDialog({ policy, onClose }: { policy: SlaPolicy | null; onClose: () => void }) {
  const qc = useQueryClient()
  const isEdit = !!policy

  const [name, setName] = useState(policy?.name ?? '')
  const [description, setDescription] = useState(policy?.description ?? '')
  const [priority, setPriority] = useState(policy?.priority ?? 'all')
  const [workType, setWorkType] = useState(policy?.work_type ?? 'all')
  const [siteId, setSiteId] = useState(policy?.site_id ?? '')
  const [responseTime, setResponseTime] = useState(String(policy?.response_time ?? 60))
  const [resolutionTime, setResolutionTime] = useState(String(policy?.resolution_time ?? 480))
  const [escalationPct, setEscalationPct] = useState(String(policy?.escalation_after_pct ?? 80))
  const [escalationTo, setEscalationTo] = useState(policy?.escalation_to ?? '')
  const [isDefault, setIsDefault] = useState(policy?.is_default ?? false)
  // Catalog scope. null = policy matches WOs from every customer.
  const [customerId, setCustomerId] = useState<string | null>(policy?.customer_id ?? null)

  const sitesQ = useQuery({ queryKey: ['sites'], queryFn: () => api.sites.list(), staleTime: 5 * 60_000 })
  const sites: any[] = Array.isArray(sitesQ.data) ? sitesQ.data : []
  const usersQ = useQuery({ queryKey: ['users'], queryFn: () => api.users.list(), staleTime: 60_000 })
  const users: any[] = Array.isArray(usersQ.data) ? usersQ.data : []

  const m = useMutation({
    mutationFn: () => {
      // On edit, a field the user just cleared must be sent as an
      // explicit `null` — omitting it (`undefined`) means "leave
      // unchanged" to the backend's sparse update, so the old value
      // would silently survive a "successful" save. `site_id` and
      // `escalation_to` are raw uuid columns with no service-side
      // ''→null conversion, so an empty string would 500 at Postgres —
      // null is required, not just preferred.
      const empty = isEdit ? null : undefined
      const body = {
        name: name.trim(), description: description.trim() || empty,
        priority, work_type: workType, site_id: siteId || empty,
        response_time: parseInt(responseTime) || 60,
        resolution_time: parseInt(resolutionTime) || 480,
        escalation_after_pct: parseInt(escalationPct) || 80,
        escalation_to: escalationTo || empty,
        is_default: isDefault,
        customer_id: customerId,
      }
      return isEdit ? api.sla.update(policy!.id, body) : api.sla.create(body as CreateSlaPolicyRequest)
    },
    onSuccess: () => { toast.success(isEdit ? 'Policy updated' : 'Policy created'); qc.invalidateQueries({ queryKey: ['sla-policies'] }); onClose() },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed'),
  })

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2.5 } }}>
      <DialogTitle sx={{ fontSize: 16, fontWeight: 700 }}>{isEdit ? 'Edit SLA policy' : 'New SLA policy'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          <TextField label="Policy name" required value={name} onChange={(e) => setName(e.target.value)} size="small" fullWidth autoFocus
            placeholder="e.g. Critical — 30min response" />
          <TextField label="Description" value={description} onChange={(e) => setDescription(e.target.value)} size="small" fullWidth
            placeholder="When does this policy apply?" />

          <CustomerScopePicker
            mode="catalog"
            value={customerId}
            onChange={setCustomerId}
            helperText="All = matches WOs from every customer. Pick one to apply this policy only to that customer's WOs."
          />

          <Divider />
          <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.secondary' }}>Matching rules</Typography>

          <Stack direction="row" spacing={2}>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary', mb: 0.5 }}>Priority</Typography>
              <Select value={priority} onChange={(e) => setPriority(e.target.value)} size="small" fullWidth>
                <MenuItem value="all">All priorities</MenuItem>
                <MenuItem value="low">Low</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="high">High</MenuItem>
                <MenuItem value="critical">Critical</MenuItem>
              </Select>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary', mb: 0.5 }}>Work type</Typography>
              <Select value={workType} onChange={(e) => setWorkType(e.target.value)} size="small" fullWidth>
                <MenuItem value="all">All types</MenuItem>
                <MenuItem value="reactive">Reactive</MenuItem>
                <MenuItem value="preventive">Preventive</MenuItem>
                <MenuItem value="inspection">Inspection</MenuItem>
                <MenuItem value="safety">Safety</MenuItem>
              </Select>
            </Box>
          </Stack>

          <Box>
            <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary', mb: 0.5 }}>Site (optional)</Typography>
            <SearchableSelect
              value={siteId}
              onChange={setSiteId}
              options={sites}
              getValue={(s: any) => s.id}
              getLabel={formatSiteName}
              placeholder="All sites"
            />
          </Box>

          <Divider />
          <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.secondary' }}>Time targets (minutes)</Typography>

          <Stack direction="row" spacing={2}>
            <TextField label="Response time" required type="number" value={responseTime} onChange={(e) => setResponseTime(e.target.value)}
              size="small" fullWidth helperText="Time to first action (minutes)" />
            <TextField label="Resolution time" required type="number" value={resolutionTime} onChange={(e) => setResolutionTime(e.target.value)}
              size="small" fullWidth helperText="Time to close (minutes)" />
          </Stack>

          <Divider />
          <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.secondary' }}>Escalation</Typography>

          <Stack direction="row" spacing={2}>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary', mb: 0.5 }}>Escalate to</Typography>
              <SearchableSelect
                value={escalationTo}
                onChange={setEscalationTo}
                options={users}
                getValue={(u: any) => u.id}
                getLabel={(u: any) => u.name}
                placeholder="No escalation"
                renderOption={(u: any) => (
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <UserAvatar name={u.name} avatarUrl={u.avatar_url} size={22} sx={{ bgcolor: '#0D9488' }} />
                    <Box>
                      <Typography sx={{ fontSize: 13 }}>{u.name}</Typography>
                      <Typography sx={{ fontSize: 10.5, color: 'text.secondary' }}>{u.role?.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase())}</Typography>
                    </Box>
                  </Stack>
                )}
              />
            </Box>
            <TextField label="Escalation threshold (%)" type="number" value={escalationPct} onChange={(e) => setEscalationPct(e.target.value)}
              size="small" sx={{ flex: 1 }} helperText="% of time elapsed before escalating" />
          </Stack>

          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Switch size="small" checked={isDefault} onChange={(e) => setIsDefault(e.target.checked)} />
            <Typography sx={{ fontSize: 13 }}>Set as default policy for this priority</Typography>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} sx={{ color: 'text.secondary' }}>Cancel</Button>
        <Button variant="contained" disabled={!name.trim() || m.isPending} onClick={() => m.mutate()}>
          {m.isPending ? 'Saving...' : isEdit ? 'Save' : 'Create policy'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
